export type PolicyBlock =
    | { type: 'text'; content: string }
    | { type: 'list'; content: string[] };

export type PolicySubsection = {
    title: string;
    blocks: PolicyBlock[];
};

export type PolicySection = {
    title: string;
    blocks: PolicyBlock[];
    subsections?: PolicySubsection[];
};

export const CONTACT_EMAIL = 'projects@3d-stockpicker.com';

export const POLICY_INTRO =
    'Welcome to Inform3D. This Privacy Policy outlines how we collect, use, and protect your ' +
    'personal information. By using the Website, you agree to the terms described in this policy.';

export const POLICY_SECTIONS: PolicySection[] = [
    {
        title: 'Information We Collect',
        blocks: [
            { type: 'text', content: 'We may collect the following types of personal information:' },
            {
                type: 'list',
                content: [
                    'Contact Information: Name, email address, and other contact details.',
                    'Account Information: Usernames, passwords, and other account-related information.',
                    'Usage Data: Information about your interactions with the Website, including pages visited and actions taken.',
                ],
            },
        ],
    },
    {
        title: 'How We Use Your Information',
        blocks: [
            { type: 'text', content: 'We use the collected information for the following purposes:' },
            {
                type: 'list',
                content: [
                    'To provide and maintain the Website.',
                    'To personalize user experience and improve our services.',
                    'To send periodic emails regarding updates, news, or related information.',
                ],
            },
        ],
    },
    {
        title: 'Cookies and Tracking Technologies',
        blocks: [
            {
                type: 'text',
                content:
                    'We use cookies and similar technologies to enhance user experience and gather information ' +
                    'about usage patterns. Users can control cookie preferences through their browser settings.',
            },
        ],
    },
    {
        title: 'Third-Party Services',
        blocks: [
            {
                type: 'text',
                content:
                    'We may use third-party services, such as Google Analytics, to collect, monitor, and analyze ' +
                    'user behavior on our website and to improve its user experience.',
            },
            {
                type: 'text',
                content:
                    'These services may collect information sent by your browser as part of a web page request, ' +
                    'such as cookies or your IP address.',
            },
        ],
    },
    {
        title: 'Data Sharing',
        blocks: [
            {
                type: 'text',
                content:
                    'We may share non-personal information with third-party service providers for the purpose of ' +
                    'improving our website and user experience. These third-party service providers are obligated ' +
                    'not to disclose or use the information for any other purpose.',
            },
        ],
    },
    {
        title: 'Data Security',
        blocks: [
            {
                type: 'text',
                content:
                    'We take reasonable measures to protect your personal information from unauthorized access, ' +
                    'disclosure, alteration, and destruction. However, no method of transmission over the internet ' +
                    'or electronic storage is entirely secure.',
            },
        ],
    },
    {
        title: 'Your Choices',
        blocks: [
            {
                type: 'text',
                content:
                    'You can choose not to provide certain information, but this may limit your ability to access ' +
                    'certain features of the Website. You can also unsubscribe from promotional emails at any time.',
            },
        ],
    },
    {
        title: "Children's Privacy",
        blocks: [
            {
                type: 'text',
                content:
                    'The Website is not intended for users under the age of 18. We do not knowingly collect ' +
                    'personal information from children.',
            },
        ],
    },
    {
        title: 'Business Development and Direct Marketing',
        blocks: [
            {
                type: 'text',
                content:
                    'Inform3D may process professional contact information for the purposes of identifying ' +
                    'potential business customers, developing business relationships and communicating with ' +
                    'businesses about services that may be relevant to their professional activities.',
            },
            {
                type: 'text',
                content:
                    'This may include information such as your name, professional position, company or ' +
                    'organisation, business email address, business telephone number, professional location and ' +
                    'publicly available professional information.',
            },
            {
                type: 'text',
                content:
                    'We may obtain this information directly from you or from legitimate business and professional ' +
                    'sources, including company websites, public business directories, professional networking ' +
                    'platforms, corporate publications, business databases and other publicly or commercially ' +
                    'available sources.',
            },
        ],
        subsections: [
            {
                title: 'Legal basis',
                blocks: [
                    {
                        type: 'text',
                        content:
                            'Where applicable, Inform3D relies on Article 6(1)(f) GDPR (legitimate interests) for ' +
                            'business development and direct marketing, not overriding the fundamental rights, ' +
                            'freedoms, and-or privacy expectations of the individual and/or business.',
                    },
                    { type: 'text', content: 'Our legitimate interests include:' },
                    {
                        type: 'list',
                        content: [
                            'identifying businesses that may benefit from our services;',
                            'developing business relationships;',
                            'communicating relevant B2B information;',
                            'maintaining accurate professional contact information; and',
                            'managing our business-development activities.',
                        ],
                    },
                    {
                        type: 'text',
                        content:
                            'Before relying on legitimate interests, we consider whether the processing is necessary ' +
                            'and proportionate and whether the interests, rights and freedoms of the individual ' +
                            'override our legitimate interests.',
                    },
                    {
                        type: 'text',
                        content:
                            'Our assessment takes into account factors such as the professional context in which ' +
                            'information is used, the nature of the information, the relevance of the communication ' +
                            "to the individual's professional role, the individual's reasonable expectations and the " +
                            'potential impact of the processing.',
                    },
                ],
            },
            {
                title: 'Direct marketing',
                blocks: [
                    {
                        type: 'text',
                        content:
                            'Where permitted by applicable law, Inform3D may contact businesses and individuals ' +
                            'acting in a professional capacity by business email, telephone or other appropriate ' +
                            'business communication channels regarding our services.',
                    },
                    {
                        type: 'text',
                        content:
                            'The use of legitimate interest under GDPR does not by itself mean that every form of ' +
                            'marketing communication is permitted. Inform3D complies with applicable national ' +
                            'direct-marketing, electronic-communications, telemarketing and do-not-contact ' +
                            'requirements in the countries in which we operate.',
                    },
                    {
                        type: 'text',
                        content:
                            'Where required, we apply appropriate checks and restrictions before making marketing ' +
                            'communications.',
                    },
                ],
            },
            {
                title: 'Data enrichment and lead qualification',
                blocks: [
                    {
                        type: 'text',
                        content:
                            'We may use professional and organisational information to verify, update, enrich and ' +
                            "qualify business contact records and to determine whether Inform3D's services may " +
                            'be relevant to a particular organisation.',
                    },
                    {
                        type: 'text',
                        content:
                            'This may involve combining information from legitimate business sources or assessing ' +
                            'publicly available information about an organisation, such as its industry, size, ' +
                            'business activities, technology environment or publicly visible business needs.',
                    },
                    {
                        type: 'text',
                        content:
                            'Such processing is used for business-development and marketing purposes and is not ' +
                            'intended to make decisions producing legal or similarly significant effects concerning ' +
                            'individuals.',
                    },
                ],
            },
            {
                title: 'CRM and communication records',
                blocks: [
                    {
                        type: 'text',
                        content:
                            'We may maintain professional contact information and records of our business ' +
                            'communications in our CRM and other business systems. These records may include ' +
                            'contact details, professional role, company information, source of the information, ' +
                            'previous communications, marketing preferences and requests not to be contacted.',
                    },
                    {
                        type: 'text',
                        content:
                            'We use this information to manage business relationships, avoid unnecessary or repeated ' +
                            'communications, maintain accurate records and respect marketing preferences.',
                    },
                ],
            },
            {
                title: 'Right to object',
                blocks: [
                    {
                        type: 'text',
                        content:
                            'You have the right to object to the processing of your personal data for direct ' +
                            'marketing at any time. If you object to direct marketing, we will stop using your ' +
                            'personal data for that purpose. We may retain a limited record of your objection where ' +
                            'necessary to ensure that we do not inadvertently contact you again.',
                    },
                    {
                        type: 'text',
                        content:
                            'You can exercise your right to object by contacting us using the contact details ' +
                            'provided in this Privacy Policy or by informing the person contacting you.',
                    },
                ],
            },
            {
                title: 'Retention',
                blocks: [
                    {
                        type: 'text',
                        content:
                            'We retain business-development and contact information only for as long as reasonably ' +
                            'necessary for the relevant business or marketing purposes. We periodically review our ' +
                            'records and delete, anonymise or restrict information that is no longer necessary or ' +
                            'relevant.',
                    },
                    {
                        type: 'text',
                        content:
                            'Information required to maintain a do-not-contact or suppression record may be retained ' +
                            'for as long as necessary to respect your objection.',
                    },
                ],
            },
        ],
    },
    {
        title: 'Changes to the Privacy Policy',
        blocks: [
            {
                type: 'text',
                content:
                    'We reserve the right to update this Privacy Policy at any time. Changes will be effective upon ' +
                    'posting, and users will be notified of significant updates.',
            },
        ],
    },
];
